import type {
  NavbarWithCtaButton,
  SitemapLinksFooterSection,
} from '@flotiq/flotiq-flowbite';
import type { BaseApiListResponse, BaseApiResponse } from './types/api-request';
import type { OnboardingApplicationHydrated } from './types/api-response';
import type {
  GeneratedForm,
  GeneratedFormFromLambda,
  MarketplacePlugin,
  Notification,
  Starter,
  Tutorial,
} from './types/external-api-response';

import { getEnv } from '../env';
import { makeExternalServiceQuery } from './base-request';

export const getStarters = makeExternalServiceQuery<
  BaseApiListResponse<Starter>
>(getEnv('VITE_FLOTIQ_STARTERS_URL') || '', 'GET', {
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Token': getEnv('VITE_FLOTIQ_STARTERS_X_AUTH_TOKEN'),
  },
});

export const getMarketplacePlugins = makeExternalServiceQuery<
  BaseApiListResponse<MarketplacePlugin>
>(getEnv('VITE_MARKETPLACE_API_URL') || '', 'GET');

export const getTutorial = makeExternalServiceQuery<
  BaseApiListResponse<Tutorial>
>(getEnv('VITE_FLOTIQ_TUTORIAL_URL') || '', 'GET', {
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Token': getEnv('VITE_FLOTIQ_TUTORIAL_X_AUTH_TOKEN'),
  },
});

const generateFormQuery = makeExternalServiceQuery<
  BaseApiResponse<GeneratedFormFromLambda>
>(getEnv('VITE_FORM_GENERATOR_URL') || '', 'POST', {}, {}, true);

export const generateForm = (jwt: string | undefined, body: any) =>
  generateFormQuery(jwt, undefined, undefined, { body: JSON.stringify(body) });

export const getFlotiqPageNav = makeExternalServiceQuery<
  BaseApiResponse<typeof NavbarWithCtaButton>
>(getEnv('VITE_FLOTIQ_PAGE_NAV_API_URL') || '', 'GET', {
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Token': getEnv('VITE_FLOTIQ_PAGE_SPACE_X_AUTH_TOKEN'),
  },
  public: getEnv('VITE_FLOTIQ_PAGE_SECTIONS_SHOW_DRAFTS') !== 'true',
});

export const getFlotiqPageFooter = makeExternalServiceQuery<
  BaseApiResponse<typeof SitemapLinksFooterSection>
>(getEnv('VITE_FLOTIQ_PAGE_FOOTER_API_URL') || '', 'GET', {
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Token': getEnv('VITE_FLOTIQ_PAGE_SPACE_X_AUTH_TOKEN'),
  },
  public: getEnv('VITE_FLOTIQ_PAGE_SECTIONS_SHOW_DRAFTS') !== 'true',
});

export const syncSpace = makeExternalServiceQuery<BaseApiResponse<undefined>>(
  getEnv('VITE_SYNC_LAMBDA_URL') || '',
  'POST',
  {
    headers: { 'Content-Type': 'application/json' },
    timeout: 600000,
  },
);

export const fetchDemoForms = makeExternalServiceQuery<
  BaseApiListResponse<GeneratedForm>
>(
  `${getEnv('VITE_FLOTIQ_API_URL') || ''}/api/v1/content/_generated_forms`,
  'GET',
  {
    headers: {
      Accept: 'application/json',
    },
  },
);

export const getOnboardingApplications = makeExternalServiceQuery<
  BaseApiListResponse<OnboardingApplicationHydrated>
>(
  getEnv('VITE_FLOTIQ_ONBOARDING_APPS_CTD_URL') || '',
  'GET',
  {
    headers: {
      Accept: 'application/json',
      'X-Auth-Token': getEnv('VITE_FLOTIQ_ONBOARDING_APPS_X_AUTH_TOKEN'),
    },
    public: getEnv('VITE_ONBOARDING_APPS_SHOW_DRAFTS') !== 'true',
  },
  { hydrate: 1 },
);

export const getNotifications = makeExternalServiceQuery<
  BaseApiListResponse<Notification>
>(getEnv('VITE_NOTIFICATIONS_URL') || '', 'GET', {
  headers: {
    Accept: 'application/json',
    'X-Auth-Token': getEnv('VITE_NOTIFICATIONS_X_AUTH_TOKEN'),
  },
  public: getEnv('VITE_NOTIFICATIONS_SHOW_DRAFTS') !== 'true',
});
